import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';

// Shared dialog for the three "name (+ optional tag) → mutation" flows used
// across breakdown documents: Save As, Duplicate, Rename. They all have the
// same shape (name input + maybe tag + name-uniqueness error surfacing), so
// this component owns the form chrome and the callers supply the mutation,
// labels, and an optional second tag input via `includeTag`.

export interface NameTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  // Renders inside DialogDescription. Pass JSX so callers can highlight a
  // source doc name with <strong>.
  description?: React.ReactNode;
  submitLabel: string;
  submittingLabel: string;
  // Seed values applied when the dialog opens. Each open resets the inputs
  // — callers don't need to track state across opens.
  initialName?: string;
  initialTag?: string;
  includeTag?: boolean;
  // Returns once the mutation resolves. Throw a string that includes
  // "already exists" (case-insensitive) and the dialog will pin it under the
  // name input instead of dismissing.
  onSubmit: (values: { name: string; tag: string | null }) => Promise<void>;
}

export function NameTagDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  submittingLabel,
  initialName = '',
  initialTag = '',
  includeTag = false,
  onSubmit,
}: NameTagDialogProps) {
  const [name, setName] = useState(initialName);
  const [tag, setTag] = useState(initialTag);
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setTag(initialTag);
      setNameError(null);
    }
    // Including initialName/initialTag in deps would reset the inputs every
    // time the parent re-renders with the same seed — we only want this on
    // open transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError('Name is required.');
      return;
    }
    setNameError(null);
    setSubmitting(true);
    try {
      await onSubmit({ name: trimmed, tag: includeTag ? tag.trim() || null : null });
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.toLowerCase().includes('already exists')) {
        setNameError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="namedialog-name" className="text-sm font-medium text-gray-700">
              Name
            </label>
            <Input
              id="namedialog-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
            />
            {nameError && <p className="text-xs text-red-600">{nameError}</p>}
          </div>
          {includeTag && (
            <div className="space-y-2">
              <label htmlFor="namedialog-tag" className="text-sm font-medium text-gray-700">
                Tag <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Input
                id="namedialog-tag"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                maxLength={60}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {submittingLabel}
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
